import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Droplets,
  Shield,
  FlaskConical,
  AlertTriangle,
  CheckCircle2,
  Building2,
  Phone,
  Mail,
  ArrowLeft,
  Download,
  ChevronRight,
  MapPin,
  FileText,
  Beaker,
  Users,
  ClipboardCheck,
  ThermometerSun,
  Activity,
  Waves,
  Zap,
  Settings,
  Target,
  Clock,
  Award
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";

// Sterile Mate Contact Info
const COMPANY_INFO = {
  name: "Sterile Mate LLC",
  tagline: "Medical, Dental, & Industrial Supplies",
  address: "396 W Greens Road Suite 1139",
  city: "Houston, TX 77067",
  phone: "181.456.3458",
  tollFree: "800.988.3892",
  email: "info@sterilemate.com",
  logo: "https://customer-assets.emergentagent.com/job_isaac-platform/artifacts/9qsxumik_Sterile%20Mate%20Logo%20%282%29.jpg"
};

// Generated Images
const IMAGES = {
  coverHero: "https://static.prod-images.emergentagent.com/jobs/ef4ba991-69a2-4ba7-8875-558c2345ffa9/images/9b3191e17de4b9c008083f8fc9e2e1575a2ed75d225c0b95260742ae982d0171.png",
  waterCategories: "https://static.prod-images.emergentagent.com/jobs/ef4ba991-69a2-4ba7-8875-558c2345ffa9/images/8434304539c01690980cb429b2dbc1e0568c7a8aa814b1dc9f2d6e307da10ff4.png",
  poorWaterEffects: "https://static.prod-images.emergentagent.com/jobs/ef4ba991-69a2-4ba7-8875-558c2345ffa9/images/d45d753e1e58ffff72568136d000fc53a5568213252beae8f9a97c120e8ffc0e.png",
  implementation: "https://static.prod-images.emergentagent.com/jobs/ef4ba991-69a2-4ba7-8875-558c2345ffa9/images/a1e08bcb9932e73d0ba91bf711a9ce1a74cd20071a4995f0d8bbdc716e16af3f.png",
  roSystem: "https://static.prod-images.emergentagent.com/jobs/ef4ba991-69a2-4ba7-8875-558c2345ffa9/images/2462b98b84daa711013b383aaabd5ee7e82d9ae663911cbd9309f174b6767f9b.png"
};

// Water Quality Parameters Table Data
const WATER_QUALITY_TABLE = [
  { parameter: "pH", unit: "pH units", utility: "6.5 - 8.5", critical: "5.0 - 8.0", steam: "7.0 - 8.5" },
  { parameter: "Total Alkalinity", unit: "mg/L CaCO₃", utility: "≤200", critical: "≤10", steam: "≤25" },
  { parameter: "Bacteria (HPC)", unit: "CFU/mL", utility: "≤200", critical: "≤10", steam: "<1" },
  { parameter: "Endotoxin", unit: "EU/mL", utility: "N/A", critical: "≤10", steam: "<0.25" },
  { parameter: "Total Organic Carbon", unit: "mg/L", utility: "≤2.0", critical: "≤0.5", steam: "≤0.5" },
  { parameter: "Conductivity", unit: "µS/cm", utility: "≤700", critical: "≤10", steam: "≤10" },
  { parameter: "Color & Turbidity", unit: "NTU", utility: "Colorless, <1", critical: "Colorless, <1", steam: "Colorless" },
  { parameter: "Hardness", unit: "mg/L CaCO₃", utility: "≤150", critical: "≤1", steam: "≤1" }
];

export default function ST108GuidePage() {
  const [showPrintView, setShowPrintView] = useState(false);
  const contentRef = useRef(null);

  const handleDownloadPDF = () => {
    // Open print dialog for PDF generation
    window.print();
  };

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          /* Hide everything except print content */
          body * {
            visibility: hidden;
          }
          .print-content, .print-content * {
            visibility: visible;
          }
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
          
          /* Page setup */
          @page {
            margin: 0.4in;
            size: letter;
          }
          
          /* Force page breaks */
          .page-break {
            page-break-before: always;
            break-before: page;
          }
          
          /* Prevent orphans and widows */
          p, li, h3, h4 {
            orphans: 3;
            widows: 3;
          }
          
          /* Keep headings with content */
          h2, h3, h4 {
            page-break-after: avoid;
            break-after: avoid;
          }
          
          /* Keep cards together */
          .print-keep-together {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          /* Keep tables together when possible */
          table {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          /* Keep grid items together */
          .grid > div {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          /* Ensure images print */
          img {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            max-width: 100%;
          }
          
          /* Ensure backgrounds print */
          .print-content {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Cover page styling */
          .print-cover {
            page-break-after: always;
            break-after: page;
            height: 100vh;
            display: flex;
            flex-direction: column;
          }
          
          /* Section styling for print */
          section {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          /* Reduce padding for print */
          .print-content section {
            padding-top: 0.5rem !important;
            padding-bottom: 0.5rem !important;
          }
          
          /* Make sure rounded corners and shadows don't cause issues */
          .rounded-2xl {
            border-radius: 8px !important;
          }
          
          /* Footer should stay at bottom */
          footer {
            page-break-before: auto;
          }
        }
      `}</style>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
        {/* Header - No Print */}
        <div className="no-print bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between py-4">
              <Link to="/aami-st108" className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline font-medium">Back to ST108 Resources</span>
              </Link>
              
              <div className="flex items-center gap-4">
                <img src={COMPANY_INFO.logo} alt="Sterile Mate Logo" className="h-10 object-contain" />
              </div>

              <Button 
                onClick={handleDownloadPDF}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="download-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content - Printable */}
        <div ref={contentRef} className="print-content">
          {/* Cover Section - Full Page for Print */}
          <section className="print-cover relative" style={{ minHeight: '100vh' }}>
            {/* Background Image - Full coverage */}
            <div className="absolute inset-0">
              <img 
                src={IMAGES.coverHero} 
                alt="Healthcare Water Quality" 
                className="w-full h-full object-cover"
                style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}
              />
              <div 
                className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-blue-800/85 to-blue-700/70"
                style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}
              ></div>
            </div>
            
            {/* Cover Content */}
            <div className="relative z-10 flex flex-col justify-between h-full min-h-[600px] md:min-h-[700px] p-8 md:p-12">
              {/* Top Section - Logo */}
              <div className="flex justify-between items-start">
                <div className="bg-white rounded-xl p-4 shadow-xl">
                  <img 
                    src={COMPANY_INFO.logo} 
                    alt="Sterile Mate" 
                    className="h-16 md:h-24 object-contain"
                    style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}
                  />
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2 text-white text-sm">
                  2025 Edition
                </div>
              </div>
              
              {/* Middle Section - Title */}
              <div className="flex-1 flex items-center">
                <div className="max-w-3xl">
                  <div className="inline-block bg-cyan-500/30 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                    <span className="text-cyan-100 font-medium text-sm uppercase tracking-wider">
                      Healthcare Water Quality Compliance
                    </span>
                  </div>
                  <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                    Your Guide to<br />
                    <span className="text-cyan-300">ANSI/AAMI ST108</span>
                  </h1>
                  <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-2xl">
                    Water Quality Standards for Medical Device Reprocessing
                  </p>
                  <div className="flex items-center gap-3 text-white bg-white/10 backdrop-blur-sm rounded-lg px-5 py-3 inline-flex">
                    <Shield className="w-6 h-6 text-cyan-300" />
                    <span className="text-lg">Ensuring Patient Safety Through Water Quality Excellence</span>
                  </div>
                </div>
              </div>
              
              {/* Bottom Section - Contact Info */}
              <div className="flex flex-col md:flex-row justify-between items-end gap-6 pt-8 border-t border-white/20">
                <div className="text-white">
                  <p className="font-bold text-xl mb-1">{COMPANY_INFO.name}</p>
                  <p className="text-blue-200 text-sm">{COMPANY_INFO.tagline}</p>
                </div>
                <div className="text-right text-white text-sm">
                  <p className="flex items-center justify-end gap-2 mb-1">
                    <Phone className="w-4 h-4 text-cyan-300" />
                    {COMPANY_INFO.phone} | Toll Free: {COMPANY_INFO.tollFree}
                  </p>
                  <p className="flex items-center justify-end gap-2 mb-1">
                    <Mail className="w-4 h-4 text-cyan-300" />
                    {COMPANY_INFO.email}
                  </p>
                  <p className="flex items-center justify-end gap-2">
                    <MapPin className="w-4 h-4 text-cyan-300" />
                    {COMPANY_INFO.address}, {COMPANY_INFO.city}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Table of Contents - New Page */}
          <section className="max-w-7xl mx-auto px-4 py-8 page-break">
            <div className="bg-white rounded-2xl shadow-lg p-5 print-keep-together print-keep-together print-keep-together">
              <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                <FileText className="w-6 h-6 text-blue-600" />
                Table of Contents
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { num: 1, title: "Understanding ANSI/AAMI ST108", page: "Overview" },
                  { num: 2, title: "Consequences of Inadequate Water Quality", page: "Risks" },
                  { num: 3, title: "Water Quality Classifications", page: "Categories" },
                  { num: 4, title: "Quality Parameters & Specifications", page: "Standards" },
                  { num: 5, title: "Facilities Requiring Compliance", page: "Scope" },
                  { num: 6, title: "Responsibilities for Facility Managers", page: "Management" },
                  { num: 7, title: "Legionella & Pathogen Control", page: "Safety" },
                  { num: 8, title: "Compliance Risks & Consequences", page: "Compliance" },
                  { num: 9, title: "Implementation Roadmap", page: "Steps" },
                  { num: 10, title: "Sterile Mate Solutions", page: "Products" }
                ].map((item) => (
                  <div key={item.num} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-blue-50 transition-colors print-keep-together">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {item.num}
                      </span>
                      <span className="font-medium text-slate-700">{item.title}</span>
                    </div>
                    <span className="text-sm text-slate-500">{item.page}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Section 1: Overview */}
          <section className="max-w-7xl mx-auto px-4 py-8 page-break">
            <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-900 rounded-2xl p-6 md:p-10 text-white mb-6 print-keep-together">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                  <span className="text-2xl font-bold">1</span>
                </div>
                <span className="text-cyan-300 font-semibold">Section One</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-3">Understanding ANSI/AAMI ST108</h2>
              <p className="text-blue-100 text-base max-w-3xl">
                A comprehensive guide to the essential water quality standard for healthcare facilities
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-5 print-keep-together print-keep-together">
              <h3 className="text-xl font-bold text-slate-800 mb-4">What is ANSI/AAMI ST108?</h3>
              <p className="text-slate-600 mb-4 leading-relaxed text-sm">
                ANSI/AAMI ST108:2023 represents the definitive American National Standard establishing comprehensive 
                requirements for water quality throughout the medical device reprocessing lifecycle. This standard 
                supersedes the previous AAMI TIR34 technical information report, transforming voluntary guidelines 
                into binding requirements for sterile processing departments across healthcare facilities.
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-blue-50 rounded-xl p-5 print-keep-together">
                  <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2 text-sm">
                    <Target className="w-5 h-5" />
                    Standard Objectives
                  </h4>
                  <ul className="space-y-2 text-slate-700 text-xs">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Establish binding water quality requirements for medical device reprocessing</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Define specifications for utility water, critical water, and steam</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Mandate multidisciplinary water management teams</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Require ongoing monitoring and documentation protocols</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-amber-50 rounded-xl p-5 print-keep-together">
                  <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2 text-sm">
                    <Zap className="w-5 h-5" />
                    Key Changes from TIR34
                  </h4>
                  <ul className="space-y-2 text-slate-700 text-xs">
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span>Transition from voluntary guidelines to binding requirements</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span>Expanded scope to include steam quality specifications</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span>Enhanced monitoring and testing frequency requirements</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span>Mandatory formation of multidisciplinary oversight teams</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-slate-100 rounded-xl p-5 print-keep-together">
                <h4 className="font-semibold text-slate-800 mb-3">Why ST108 Matters</h4>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Water quality directly impacts patient safety, equipment longevity, and operational efficiency in healthcare facilities. 
                  Contaminants in water can lead to Healthcare-Associated Infections (HAIs), equipment damage, and compromised sterilization 
                  processes. ST108 provides the framework to prevent these issues through systematic water quality management, ensuring 
                  medical devices are properly cleaned and sterilized to protect patients and staff.
                </p>
              </div>

              {/* Cost Rectification Callout */}
              <div className="bg-gradient-to-r from-emerald-50 to-cyan-50 border border-emerald-200 rounded-xl p-5 mt-6 print-keep-together">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-emerald-900 mb-2">AskIsaac Cost Rectification</h4>
                    <p className="text-slate-700 text-sm leading-relaxed mb-3">
                      Many vendors push <strong>over-specced FDA 510K-compliant systems</strong> where they&apos;re not needed, costing hospitals 
                      <strong className="text-emerald-700"> $500K+ more than necessary</strong> per department. AskIsaac uses data-driven 
                      validation to right-size every ST108 implementation — saving over <strong className="text-emerald-700">$1M per department</strong> 
                      while maintaining full regulatory compliance.
                    </p>
                    <p className="text-emerald-600 text-sm font-medium">
                      Software-backed validation, not hardware-heavy legacy quotes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: Consequences of Poor Water Quality */}
          <section className="max-w-7xl mx-auto px-4 py-8 page-break">
            <div className="bg-gradient-to-br from-red-900 via-orange-800 to-amber-900 rounded-2xl p-6 md:p-10 text-white mb-6 print-keep-together">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                  <span className="text-2xl font-bold">2</span>
                </div>
                <span className="text-amber-300 font-semibold">Section Two</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Consequences of Inadequate Water Quality</h2>
              <p className="text-orange-100 text-lg max-w-3xl">
                Understanding the risks when water quality standards are not maintained
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <img 
                src={IMAGES.poorWaterEffects} 
                alt="Effects of Poor Water Quality" 
                className="rounded-2xl shadow-lg w-full h-64 object-cover"
              />
              
              <div className="bg-white rounded-2xl shadow-lg p-5 print-keep-together">
                <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Critical Impact Areas
                </h3>
                <p className="text-slate-600 mb-4 text-sm">
                  Inadequate water quality in healthcare settings can have far-reaching consequences across multiple domains:
                </p>
                <div className="space-y-3">
                  <div className="p-3 bg-red-50 rounded-lg border-l-4 border-red-500">
                    <h5 className="font-semibold text-red-900">Patient Safety</h5>
                    <p className="text-sm text-slate-600">Risk of HAIs, endotoxin reactions, and contamination exposure</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg border-l-4 border-amber-500">
                    <h5 className="font-semibold text-amber-900">Equipment Damage</h5>
                    <p className="text-sm text-slate-600">Corrosion, scaling, and premature failure of costly medical equipment</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                    <h5 className="font-semibold text-blue-900">Process Integrity</h5>
                    <p className="text-sm text-slate-600">Compromised cleaning and sterilization effectiveness</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-5 print-keep-together print-keep-together">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-red-50 rounded-xl">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-red-600" />
                  </div>
                  <h4 className="font-bold text-red-900 mb-2">Patient Risks</h4>
                  <ul className="text-sm text-slate-600 space-y-1 text-left">
                    <li>• Healthcare-Associated Infections</li>
                    <li>• Pyrogenic reactions from endotoxins</li>
                    <li>• Chemical toxicity exposure</li>
                    <li>• Residual contamination on devices</li>
                  </ul>
                </div>

                <div className="text-center p-6 bg-amber-50 rounded-xl">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Settings className="w-8 h-8 text-amber-600" />
                  </div>
                  <h4 className="font-bold text-amber-900 mb-2">Equipment Impact</h4>
                  <ul className="text-sm text-slate-600 space-y-1 text-left">
                    <li>• Mineral scale accumulation</li>
                    <li>• Metal corrosion and pitting</li>
                    <li>• Biofilm formation in systems</li>
                    <li>• Reduced operational lifespan</li>
                  </ul>
                </div>

                <div className="text-center p-6 bg-blue-50 rounded-xl">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FlaskConical className="w-8 h-8 text-blue-600" />
                  </div>
                  <h4 className="font-bold text-blue-900 mb-2">Process Effects</h4>
                  <ul className="text-sm text-slate-600 space-y-1 text-left">
                    <li>• Reduced detergent effectiveness</li>
                    <li>• Incomplete soil removal</li>
                    <li>• Spotting and staining on devices</li>
                    <li>• Sterilization failures</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3: Water Quality Classifications */}
          <section className="max-w-7xl mx-auto px-4 py-8 page-break">
            <div className="bg-gradient-to-br from-cyan-900 via-blue-800 to-indigo-900 rounded-2xl p-6 md:p-10 text-white mb-6 print-keep-together">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                  <span className="text-2xl font-bold">3</span>
                </div>
                <span className="text-cyan-300 font-semibold">Section Three</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Water Quality Classifications</h2>
              <p className="text-blue-100 text-lg max-w-3xl">
                Understanding the three essential categories of water in medical device reprocessing
              </p>
            </div>

            <div className="mb-8">
              <img 
                src={IMAGES.waterCategories} 
                alt="Water Quality Categories" 
                className="rounded-2xl shadow-lg w-full h-72 object-cover"
              />
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl shadow-lg p-5 print-keep-together border-t-4 border-blue-500">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <Droplets className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-blue-900 mb-3">Utility Water</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Potable water meeting EPA drinking water standards. Serves as the foundation for general cleaning 
                  operations and as feed water for treatment systems.
                </p>
                <div className="bg-blue-50 rounded-lg p-4">
                  <h5 className="font-semibold text-blue-800 text-sm mb-2">Common Applications:</h5>
                  <ul className="text-xs text-slate-600 space-y-1">
                    <li>• Initial instrument rinsing</li>
                    <li>• General facility cleaning</li>
                    <li>• Feed water for RO systems</li>
                    <li>• Non-critical washing processes</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-5 print-keep-together border-t-4 border-cyan-500">
                <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-4">
                  <Beaker className="w-7 h-7 text-cyan-600" />
                </div>
                <h3 className="text-xl font-bold text-cyan-900 mb-3">Critical Water</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Highly purified water produced through reverse osmosis and deionization processes. Essential for 
                  final rinsing of instruments and critical applications.
                </p>
                <div className="bg-cyan-50 rounded-lg p-4">
                  <h5 className="font-semibold text-cyan-800 text-sm mb-2">Common Applications:</h5>
                  <ul className="text-xs text-slate-600 space-y-1">
                    <li>• Final rinse for reprocessed devices</li>
                    <li>• Automated washer final cycles</li>
                    <li>• Semi-critical device processing</li>
                    <li>• Critical device preparation</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-5 print-keep-together border-t-4 border-purple-500">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                  <ThermometerSun className="w-7 h-7 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-purple-900 mb-3">Steam</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Vapor-phase water used in sterilization processes. Quality depends on feedwater purity and 
                  generation system maintenance.
                </p>
                <div className="bg-purple-50 rounded-lg p-4">
                  <h5 className="font-semibold text-purple-800 text-sm mb-2">Common Applications:</h5>
                  <ul className="text-xs text-slate-600 space-y-1">
                    <li>• Steam sterilization cycles</li>
                    <li>• Autoclaving processes</li>
                    <li>• Terminal sterilization</li>
                    <li>• Sterilizer jacket heating</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4: Quality Parameters Table */}
          <section className="max-w-7xl mx-auto px-4 py-8 page-break">
            <div className="bg-gradient-to-br from-green-900 via-emerald-800 to-teal-900 rounded-2xl p-6 md:p-10 text-white mb-6 print-keep-together">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                  <span className="text-2xl font-bold">4</span>
                </div>
                <span className="text-emerald-300 font-semibold">Section Four</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Quality Parameters & Specifications</h2>
              <p className="text-green-100 text-lg max-w-3xl">
                Reference table of recommended water quality levels for medical device reprocessing
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-800 text-white">
                      <th className="px-6 py-4 text-left font-semibold">Parameter</th>
                      <th className="px-6 py-4 text-left font-semibold">Unit</th>
                      <th className="px-6 py-4 text-center font-semibold bg-blue-700">Utility Water</th>
                      <th className="px-6 py-4 text-center font-semibold bg-cyan-700">Critical Water</th>
                      <th className="px-6 py-4 text-center font-semibold bg-purple-700">Steam</th>
                    </tr>
                  </thead>
                  <tbody>
                    {WATER_QUALITY_TABLE.map((row, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                        <td className="px-6 py-4 font-medium text-slate-800">{row.parameter}</td>
                        <td className="px-6 py-4 text-slate-600 text-sm">{row.unit}</td>
                        <td className="px-6 py-4 text-center text-blue-700 font-medium">{row.utility}</td>
                        <td className="px-6 py-4 text-center text-cyan-700 font-medium">{row.critical}</td>
                        <td className="px-6 py-4 text-center text-purple-700 font-medium">{row.steam}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-4 bg-slate-100 text-sm text-slate-600">
                <strong>Note:</strong> These values represent recommended limits based on ANSI/AAMI ST108 guidelines. 
                Specific requirements may vary based on medical device manufacturer instructions and facility protocols.
              </div>
            </div>
          </section>

          {/* Section 5: Facilities Requiring Compliance */}
          <section className="max-w-7xl mx-auto px-4 py-8 page-break">
            <div className="bg-gradient-to-br from-indigo-900 via-violet-800 to-purple-900 rounded-2xl p-6 md:p-10 text-white mb-6 print-keep-together">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                  <span className="text-2xl font-bold">5</span>
                </div>
                <span className="text-violet-300 font-semibold">Section Five</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Facilities Requiring Compliance</h2>
              <p className="text-violet-100 text-lg max-w-3xl">
                Healthcare facilities impacted by ANSI/AAMI ST108 requirements
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Building2, title: "Major Medical Centers", desc: "Large hospital systems with central sterile processing departments" },
                { icon: Activity, title: "Community Hospitals", desc: "Regional and local hospitals with instrument reprocessing needs" },
                { icon: Target, title: "Ambulatory Surgery Centers", desc: "Outpatient surgical facilities requiring instrument sterilization" },
                { icon: Users, title: "Specialty Clinics", desc: "Dental, dialysis, and specialty practices with reprocessing requirements" }
              ].map((facility, idx) => {
                const Icon = facility.icon;
                return (
                  <div key={idx} className="bg-white rounded-2xl shadow-lg p-5 print-keep-together text-center">
                    <div className="w-16 h-16 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-indigo-600" />
                    </div>
                    <h3 className="font-bold text-slate-800 mb-2">{facility.title}</h3>
                    <p className="text-sm text-slate-600">{facility.desc}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Section 6: Facility Manager Responsibilities */}
          <section className="max-w-7xl mx-auto px-4 py-12">
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 rounded-2xl p-6 md:p-10 text-white mb-6 print-keep-together">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                  <span className="text-2xl font-bold">6</span>
                </div>
                <span className="text-slate-300 font-semibold">Section Six</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Responsibilities for Facility Managers</h2>
              <p className="text-slate-300 text-lg max-w-3xl">
                Key obligations and oversight requirements under the new standard
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-5 print-keep-together print-keep-together">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <ClipboardCheck className="w-5 h-5 text-blue-600" />
                    Management Obligations
                  </h3>
                  <ul className="space-y-3">
                    {[
                      "Establish and lead a multidisciplinary water management team",
                      "Develop and maintain comprehensive water management plans",
                      "Ensure regular water quality testing and documentation",
                      "Implement corrective action procedures for quality deviations",
                      "Coordinate with Infection Prevention and Biomed departments",
                      "Maintain equipment maintenance schedules and records"
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Multidisciplinary Team
                  </h3>
                  <p className="text-slate-600 mb-4 text-sm">
                    ST108 mandates formation of a water management team including representatives from:
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      "Sterile Processing",
                      "Facilities/Engineering",
                      "Infection Prevention",
                      "Clinical Engineering",
                      "Quality Assurance",
                      "Environmental Services"
                    ].map((dept, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg text-center">
                        <span className="text-sm font-medium text-slate-700">{dept}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 7: Legionella & Pathogen Control */}
          <section className="max-w-7xl mx-auto px-4 py-8 page-break">
            <div className="bg-gradient-to-br from-red-900 via-rose-800 to-pink-900 rounded-2xl p-6 md:p-10 text-white mb-6 print-keep-together">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                  <span className="text-2xl font-bold">7</span>
                </div>
                <span className="text-rose-300 font-semibold">Section Seven</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Legionella & Pathogen Control</h2>
              <p className="text-rose-100 text-lg max-w-3xl">
                Critical requirements for waterborne pathogen management in healthcare settings
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-5 print-keep-together print-keep-together">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-slate-800 mb-4">CMS Requirements</h3>
                  <p className="text-slate-600 mb-4">
                    The Centers for Medicare & Medicaid Services (CMS) requires all facilities serving Medicare 
                    and Medicaid patients to maintain water management programs that address Legionella and other 
                    waterborne pathogens.
                  </p>
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                    <h4 className="font-semibold text-red-900 mb-2">Mandatory Elements:</h4>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Written water management program</li>
                      <li>• Risk assessments for water systems</li>
                      <li>• Routine testing protocols</li>
                      <li>• Documented corrective actions</li>
                    </ul>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800 mb-4">Key Pathogens of Concern</h3>
                  <div className="space-y-3">
                    {[
                      { name: "Legionella pneumophila", risk: "Causes Legionnaires' disease; thrives in warm water systems" },
                      { name: "Pseudomonas aeruginosa", risk: "Opportunistic pathogen; forms biofilms in water systems" },
                      { name: "Mycobacterium species", risk: "Resistant to standard disinfection; found in biofilms" },
                      { name: "Stenotrophomonas", risk: "Emerging pathogen; associated with immunocompromised patients" }
                    ].map((pathogen, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                        <h5 className="font-semibold text-slate-800 text-sm">{pathogen.name}</h5>
                        <p className="text-xs text-slate-600">{pathogen.risk}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 8: Compliance Risks */}
          <section className="max-w-7xl mx-auto px-4 py-12">
            <div className="bg-gradient-to-br from-amber-900 via-orange-800 to-yellow-900 rounded-2xl p-6 md:p-10 text-white mb-6 print-keep-together">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                  <span className="text-2xl font-bold">8</span>
                </div>
                <span className="text-amber-300 font-semibold">Section Eight</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Compliance Risks & Consequences</h2>
              <p className="text-amber-100 text-lg max-w-3xl">
                Understanding the serious implications of non-compliance
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-5 print-keep-together print-keep-together">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="p-6 bg-red-50 rounded-xl border-2 border-red-200">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="font-bold text-red-900 mb-3">Patient Harm</h3>
                  <ul className="text-sm text-slate-600 space-y-2">
                    <li>• Healthcare-associated infections</li>
                    <li>• Adverse reactions and complications</li>
                    <li>• Extended hospital stays</li>
                    <li>• Potential fatalities</li>
                  </ul>
                </div>

                <div className="p-6 bg-amber-50 rounded-xl border-2 border-amber-200">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                    <Award className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="font-bold text-amber-900 mb-3">Accreditation Loss</h3>
                  <ul className="text-sm text-slate-600 space-y-2">
                    <li>• Joint Commission citations</li>
                    <li>• CMS deficiencies</li>
                    <li>• State health department actions</li>
                    <li>• Loss of operating status</li>
                  </ul>
                </div>

                <div className="p-6 bg-blue-50 rounded-xl border-2 border-blue-200">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-blue-900 mb-3">Legal Liability</h3>
                  <ul className="text-sm text-slate-600 space-y-2">
                    <li>• Medical malpractice claims</li>
                    <li>• Negligence lawsuits</li>
                    <li>• Regulatory fines and penalties</li>
                    <li>• Reputational damage</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 9: Implementation Roadmap */}
          <section className="max-w-7xl mx-auto px-4 py-8 page-break">
            <div className="bg-gradient-to-br from-emerald-900 via-green-800 to-teal-900 rounded-2xl p-6 md:p-10 text-white mb-6 print-keep-together">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                  <span className="text-2xl font-bold">9</span>
                </div>
                <span className="text-emerald-300 font-semibold">Section Nine</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Implementation Roadmap</h2>
              <p className="text-green-100 text-lg max-w-3xl">
                Step-by-step guide to achieving AAMI ST108 compliance
              </p>
            </div>

            <div className="mb-8">
              <img 
                src={IMAGES.implementation} 
                alt="Implementation Team" 
                className="rounded-2xl shadow-lg w-full h-64 object-cover"
              />
            </div>

            <div className="space-y-6">
              {[
                {
                  step: 1,
                  title: "Water Quality Assessment",
                  items: [
                    "Conduct comprehensive baseline water testing",
                    "Identify all water use points in sterile processing",
                    "Form multidisciplinary water management team",
                    "Document current water quality parameters"
                  ]
                },
                {
                  step: 2,
                  title: "Treatment System Implementation",
                  items: [
                    "Evaluate current treatment capabilities",
                    "Specify required equipment (RO, DI, UV, filtration)",
                    "Install and commission treatment systems",
                    "Validate system performance"
                  ]
                },
                {
                  step: 3,
                  title: "Quality Assurance Program",
                  items: [
                    "Establish testing schedules and protocols",
                    "Define acceptable limits for all parameters",
                    "Implement documentation procedures",
                    "Train staff on monitoring responsibilities"
                  ]
                },
                {
                  step: 4,
                  title: "Ongoing Monitoring & Maintenance",
                  items: [
                    "Perform daily conductivity and visual checks",
                    "Conduct periodic microbial testing",
                    "Maintain equipment per manufacturer guidelines",
                    "Review and update procedures as needed"
                  ]
                }
              ].map((phase) => (
                <div key={phase.step} className="bg-white rounded-2xl shadow-lg p-5 print-keep-together flex gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-emerald-600 rounded-xl flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">{phase.step}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-800 mb-3">{phase.title}</h3>
                    <div className="grid md:grid-cols-2 gap-2">
                      {phase.items.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-slate-600">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 10: Sterile Mate Solutions */}
          <section className="max-w-7xl mx-auto px-4 py-8 page-break">
            <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-900 rounded-2xl p-6 md:p-10 text-white mb-6 print-keep-together">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                  <span className="text-2xl font-bold">10</span>
                </div>
                <span className="text-cyan-300 font-semibold">Section Ten</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Sterile Mate Solutions</h2>
              <p className="text-blue-100 text-lg max-w-3xl">
                Comprehensive water purification and testing services for AAMI ST108 compliance
              </p>
            </div>

            <div className="mb-8">
              <img 
                src={IMAGES.roSystem} 
                alt="Sterile Mate RO System" 
                className="rounded-2xl shadow-lg w-full h-72 object-cover"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Water Purification Systems */}
              <div className="bg-white rounded-2xl shadow-lg p-5 print-keep-together">
                <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Droplets className="w-5 h-5 text-blue-600" />
                  Water Purification Systems
                </h3>
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-xl">
                    <h4 className="font-semibold text-blue-900 mb-2">PURETEC ROS-IXC Reverse Osmosis</h4>
                    <p className="text-sm text-slate-600 mb-2">
                      Medical-grade RO systems engineered for AAMI ST108 compliance with up to 99.4% purification 
                      and 80% recovery rates.
                    </p>
                    <ul className="text-xs text-slate-500 space-y-1">
                      <li>• Vertical configuration, minimal footprint</li>
                      <li>• 2,100 - 15,600 GPD capacity options</li>
                      <li>• Remote monitoring and reporting</li>
                      <li>• Made in USA</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-cyan-50 rounded-xl">
                    <h4 className="font-semibold text-cyan-900 mb-2">Portable Exchange Deionizers</h4>
                    <p className="text-sm text-slate-600 mb-2">
                      Cost-effective DI service with no capital investment. NSF, WQA, and FDA approved.
                    </p>
                    <ul className="text-xs text-slate-500 space-y-1">
                      <li>• Tank sizes from 0.3 to 35 cubic feet</li>
                      <li>• Quick-response tank exchanges</li>
                      <li>• In-house resin processing</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-purple-50 rounded-xl">
                    <h4 className="font-semibold text-purple-900 mb-2">Water Softeners & Pre-treatment</h4>
                    <p className="text-sm text-slate-600 mb-2">
                      FRP tanks with 150 psi rating, fully automatic operation, and premium cation exchange resin.
                    </p>
                    <ul className="text-xs text-slate-500 space-y-1">
                      <li>• Prevents scale buildup</li>
                      <li>• Protects RO membranes</li>
                      <li>• Extends equipment life</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Water Testing Services */}
              <div className="bg-white rounded-2xl shadow-lg p-5 print-keep-together">
                <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <FlaskConical className="w-5 h-5 text-blue-600" />
                  Water Testing Services
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  Through our partnership with A & B Labs (ISO/IEC 17025:2017 accredited), we provide comprehensive 
                  water quality testing services:
                </p>
                <div className="space-y-2">
                  {[
                    { test: "pH & Corrosivity", method: "SW-846 9041A", tat: "5 Days" },
                    { test: "Conductivity", method: "SM 2510B", tat: "5 Days" },
                    { test: "Total Organic Carbon", method: "SW-846 9060A", tat: "5 Days" },
                    { test: "HPC Bacteria", method: "SM 9215B/D", tat: "5 Days" },
                    { test: "Hardness", method: "SM 2340C", tat: "5 Days" },
                    { test: "Alkalinity", method: "SM 2320B", tat: "5 Days" },
                    { test: "Metals (Fe, Cu, Mn)", method: "EPA 200.7", tat: "5 Days" },
                    { test: "Endotoxins", method: "LAL", tat: "Contact Us" }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span className="font-medium text-slate-700">{item.test}</span>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span>{item.method}</span>
                        <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{item.tat}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-emerald-50 rounded-lg">
                  <p className="text-xs text-emerald-800">
                    <strong>Lab Certifications:</strong> ISO/IEC 17025:2017, CDC Legionella Elite Program, 
                    DoD ELAP, TCEQ Approved, NELAP (TX & LA)
                  </p>
                </div>
              </div>
            </div>

            {/* Services */}
            <div className="bg-white rounded-2xl shadow-lg p-5 print-keep-together print-keep-together mb-8">
              <h3 className="text-xl font-bold text-slate-800 mb-6 text-center">Complete Project Services</h3>
              <div className="grid md:grid-cols-4 gap-6">
                {[
                  { icon: Settings, title: "System Design", desc: "Custom water treatment system engineering" },
                  { icon: Waves, title: "Installation", desc: "Professional equipment installation and commissioning" },
                  { icon: Clock, title: "Maintenance", desc: "Preventive maintenance and emergency support" },
                  { icon: Activity, title: "Monitoring", desc: "24/7 remote monitoring and data reporting" }
                ].map((service, idx) => {
                  const Icon = service.icon;
                  return (
                    <div key={idx} className="text-center">
                      <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                        <Icon className="w-7 h-7 text-blue-600" />
                      </div>
                      <h4 className="font-semibold text-slate-800 mb-1">{service.title}</h4>
                      <p className="text-sm text-slate-600">{service.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Contact Section */}
            <div className="bg-gradient-to-r from-blue-900 to-cyan-900 rounded-2xl p-8 text-white">
              <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                <div className="flex items-center gap-6">
                  <img src={COMPANY_INFO.logo} alt="Sterile Mate" className="h-20 bg-white rounded-xl p-2" />
                  <div>
                    <h3 className="text-2xl font-bold mb-1">{COMPANY_INFO.name}</h3>
                    <p className="text-blue-200">{COMPANY_INFO.tagline}</p>
                  </div>
                </div>
                <div className="text-center md:text-right">
                  <div className="flex flex-col sm:flex-row items-center gap-4 mb-4">
                    <a href={`tel:${COMPANY_INFO.phone}`} className="flex items-center gap-2 text-white hover:text-cyan-300">
                      <Phone className="w-5 h-5" />
                      <span className="font-semibold">{COMPANY_INFO.phone}</span>
                    </a>
                    <span className="text-blue-400">|</span>
                    <a href={`tel:${COMPANY_INFO.tollFree}`} className="flex items-center gap-2 text-white hover:text-cyan-300">
                      <span className="text-sm">Toll Free:</span>
                      <span className="font-semibold">{COMPANY_INFO.tollFree}</span>
                    </a>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <a href={`mailto:${COMPANY_INFO.email}`} className="flex items-center gap-2 text-blue-200 hover:text-white">
                      <Mail className="w-5 h-5" />
                      <span>{COMPANY_INFO.email}</span>
                    </a>
                    <div className="flex items-center gap-2 text-blue-200">
                      <MapPin className="w-5 h-5" />
                      <span>{COMPANY_INFO.address}, {COMPANY_INFO.city}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Footer */}
          <footer className="bg-slate-900 text-white py-8 mt-12">
            <div className="max-w-7xl mx-auto px-4 text-center">
              <img src={COMPANY_INFO.logo} alt="Sterile Mate" className="h-12 mx-auto mb-4 bg-white rounded-lg p-1" />
              <p className="text-slate-400 text-sm mb-2">
                © {new Date().getFullYear()} {COMPANY_INFO.name}. All Rights Reserved.
              </p>
              <p className="text-slate-500 text-xs">
                This guide is provided for informational purposes. Please consult the official ANSI/AAMI ST108 
                standard for complete requirements.
              </p>
            </div>
          </footer>
        </div>
      </div>
    </>
  );
}
